import { getAnimationIntensity, shouldEnableParticles } from '../utils/themeManager';

// Simple particle types for the demo component
export type ParticleType = 'confetti' | 'fireworks' | 'stars' | 'sparkles' | 'yatzy';

// Configuration for particle effects
export interface ParticleConfig {
  duration?: number;
  particleCount?: number;
  colors?: string[];
  size?: number;
  speed?: number;
}

// Enhanced particle types
export enum ParticleEffectType {
  YATZY = 'yatzy',
  SUPER_YATZY = 'superYatzy',
  ACHIEVEMENT = 'achievement',
  RECORD = 'record',
  BONUS = 'bonus',
  STRAIGHT = 'straight',
  FULL_HOUSE = 'fullHouse',
  SCORE_MILESTONE = 'scoreMilestone',
  LEVEL_UP = 'levelUp',
  COMBO = 'combo',
  DICE_EXPLOSION = 'diceExplosion',
  CONFETTI = 'confetti',
}

// Mapping effects to sounds
export const particleEffectSounds: Record<ParticleEffectType, string> = {
  [ParticleEffectType.YATZY]: 'yatzy',
  [ParticleEffectType.SUPER_YATZY]: 'superYatzy',
  [ParticleEffectType.ACHIEVEMENT]: 'achievement',
  [ParticleEffectType.RECORD]: 'highScore',
  [ParticleEffectType.BONUS]: 'bonus',
  [ParticleEffectType.STRAIGHT]: 'straight',
  [ParticleEffectType.FULL_HOUSE]: 'fullHouse',
  [ParticleEffectType.SCORE_MILESTONE]: 'milestone',
  [ParticleEffectType.LEVEL_UP]: 'levelUp',
  [ParticleEffectType.COMBO]: 'combo',
  [ParticleEffectType.DICE_EXPLOSION]: 'diceImpact',
  [ParticleEffectType.CONFETTI]: 'celebration',
};

// Trigger a particle effect with corresponding sound
export const triggerEffect = (
  effectType: ParticleEffectType, 
  x?: number, 
  y?: number, 
  soundVolume: number = 1.0
): void => {
  // Only proceed if particle effects are enabled in the theme
  if (!shouldEnableParticles()) return;
  
  // Create a new custom event to trigger the particle effect
  const event = new CustomEvent('triggerParticleEffect', {
    detail: {
      type: effectType,
      x,
      y,
      intensity: getAnimationIntensity(),
    },
  });
  
  // Dispatch the event
  document.dispatchEvent(event);
  
  // Play associated sound effect if available
  // Vi använder bara ljudeffekter i demo-implementationen
  // const soundId = particleEffectSounds[effectType];
  // if (soundId) {
  //   // soundManager-metoder tillgängliga: playDiceRoll, playDiceLock, playDiceUnlock, etc.
  // }
};

// Color palettes for different effects
export const effectColorPalettes: Record<ParticleEffectType, string[]> = {
  [ParticleEffectType.YATZY]: ['#FFD700', '#FFA500', '#FF4500', '#FF0000', '#FF00FF'],
  [ParticleEffectType.SUPER_YATZY]: ['#00FFFF', '#00BFFF', '#1E90FF', '#0000FF', '#8A2BE2'],
  [ParticleEffectType.ACHIEVEMENT]: ['#FFD700', '#FFC0CB', '#FF69B4', '#FF1493', '#C71585'],
  [ParticleEffectType.RECORD]: ['#00FF00', '#32CD32', '#7CFC00', '#ADFF2F', '#9ACD32'],
  [ParticleEffectType.BONUS]: ['#FF00FF', '#BA55D3', '#9370DB', '#8A2BE2', '#9400D3'],
  [ParticleEffectType.STRAIGHT]: ['#00FFFF', '#7FFFD4', '#40E0D0', '#48D1CC', '#00CED1'],
  [ParticleEffectType.FULL_HOUSE]: ['#FF7F50', '#FF6347', '#FF4500', '#FF8C00', '#FFA500'],
  [ParticleEffectType.SCORE_MILESTONE]: ['#FFFFFF', '#F8F8FF', '#F5F5F5', '#DCDCDC', '#D3D3D3'],
  [ParticleEffectType.LEVEL_UP]: ['#32CD32', '#3CB371', '#2E8B57', '#008000', '#006400'],
  [ParticleEffectType.COMBO]: ['#FF69B4', '#FF1493', '#DB7093', '#C71585', '#FF00FF'],
  [ParticleEffectType.DICE_EXPLOSION]: ['#FFFFFF', '#F5F5F5', '#EEEEEE', '#E0E0E0', '#DCDCDC'],
  [ParticleEffectType.CONFETTI]: ['#FF0000', '#FF7F00', '#FFFF00', '#00FF00', '#0000FF', '#4B0082', '#8B00FF'],
};

// Particle shape configurations for different effects
export enum ParticleShape {
  CIRCLE = 'circle',
  SQUARE = 'square',
  STAR = 'star',
  HEART = 'heart',
  DIAMOND = 'diamond',
  DICE = 'dice',
  EMOJI = 'emoji',
}

export const effectShapeConfig: Record<ParticleEffectType, ParticleShape[]> = {
  [ParticleEffectType.YATZY]: [ParticleShape.STAR, ParticleShape.CIRCLE],
  [ParticleEffectType.SUPER_YATZY]: [ParticleShape.STAR, ParticleShape.DIAMOND],
  [ParticleEffectType.ACHIEVEMENT]: [ParticleShape.STAR, ParticleShape.CIRCLE],
  [ParticleEffectType.RECORD]: [ParticleShape.DIAMOND, ParticleShape.STAR],
  [ParticleEffectType.BONUS]: [ParticleShape.CIRCLE, ParticleShape.DIAMOND],
  [ParticleEffectType.STRAIGHT]: [ParticleShape.SQUARE, ParticleShape.DIAMOND],
  [ParticleEffectType.FULL_HOUSE]: [ParticleShape.DIAMOND, ParticleShape.CIRCLE],
  [ParticleEffectType.SCORE_MILESTONE]: [ParticleShape.CIRCLE, ParticleShape.SQUARE],
  [ParticleEffectType.LEVEL_UP]: [ParticleShape.STAR, ParticleShape.DIAMOND],
  [ParticleEffectType.COMBO]: [ParticleShape.HEART, ParticleShape.STAR],
  [ParticleEffectType.DICE_EXPLOSION]: [ParticleShape.DICE],
  [ParticleEffectType.CONFETTI]: [ParticleShape.SQUARE, ParticleShape.CIRCLE],
};

// Effect parameters for different particle types
export interface ParticleEffectParameters {
  count: number;
  size: { min: number; max: number };
  speed: { min: number; max: number };
  gravity: number;
  life: { min: number; max: number };
  spread: number;
  fadeOut: boolean;
  bounce: boolean;
  drift: number;
  spin: boolean;
  emojis?: string[];
}

export const effectParameters: Record<ParticleEffectType, ParticleEffectParameters> = {
  [ParticleEffectType.YATZY]: {
    count: 100,
    size: { min: 5, max: 8 },
    speed: { min: 3, max: 8 },
    gravity: 0.1,
    life: { min: 80, max: 120 },
    spread: 360,
    fadeOut: true,
    bounce: false,
    drift: 0.5,
    spin: true,
  },
  [ParticleEffectType.SUPER_YATZY]: {
    count: 200,
    size: { min: 6, max: 10 },
    speed: { min: 4, max: 10 },
    gravity: 0.05,
    life: { min: 100, max: 150 },
    spread: 360,
    fadeOut: true,
    bounce: false,
    drift: 1,
    spin: true,
  },
  [ParticleEffectType.ACHIEVEMENT]: {
    count: 60,
    size: { min: 4, max: 7 },
    speed: { min: 3, max: 6 },
    gravity: 0.1,
    life: { min: 60, max: 100 },
    spread: 180,
    fadeOut: true,
    bounce: false,
    drift: 0.3,
    spin: true,
    emojis: ['🏆', '⭐', '🌟', '✨'],
  },
  [ParticleEffectType.RECORD]: {
    count: 120,
    size: { min: 4, max: 8 },
    speed: { min: 2, max: 7 },
    gravity: 0.1,
    life: { min: 80, max: 120 },
    spread: 270,
    fadeOut: true,
    bounce: false,
    drift: 0.5,
    spin: true,
  },
  [ParticleEffectType.BONUS]: {
    count: 50,
    size: { min: 3, max: 6 },
    speed: { min: 2, max: 5 },
    gravity: 0.15,
    life: { min: 50, max: 80 },
    spread: 120,
    fadeOut: true,
    bounce: false,
    drift: 0.2,
    spin: true,
  },
  [ParticleEffectType.STRAIGHT]: {
    count: 60,
    size: { min: 3, max: 5 },
    speed: { min: 2, max: 4 },
    gravity: 0.1,
    life: { min: 60, max: 90 },
    spread: 180,
    fadeOut: true,
    bounce: true,
    drift: 0.1,
    spin: false,
  },
  [ParticleEffectType.FULL_HOUSE]: {
    count: 70,
    size: { min: 4, max: 7 },
    speed: { min: 2, max: 5 },
    gravity: 0.12,
    life: { min: 60, max: 90 },
    spread: 160,
    fadeOut: true,
    bounce: false,
    drift: 0.3,
    spin: true,
  },
  [ParticleEffectType.SCORE_MILESTONE]: {
    count: 40,
    size: { min: 3, max: 6 },
    speed: { min: 1, max: 4 },
    gravity: 0.08,
    life: { min: 40, max: 70 },
    spread: 90,
    fadeOut: true,
    bounce: false,
    drift: 0.2,
    spin: false,
  },
  [ParticleEffectType.LEVEL_UP]: {
    count: 80,
    size: { min: 4, max: 7 },
    speed: { min: 2, max: 6 },
    gravity: 0.05,
    life: { min: 70, max: 100 },
    spread: 180,
    fadeOut: true,
    bounce: true,
    drift: 0.4,
    spin: true,
  },
  [ParticleEffectType.COMBO]: {
    count: 60,
    size: { min: 4, max: 6 },
    speed: { min: 2, max: 5 },
    gravity: 0.1,
    life: { min: 50, max: 80 },
    spread: 120,
    fadeOut: true,
    bounce: false,
    drift: 0.3,
    spin: true,
    emojis: ['🔥', '⚡', '💥', '💫'],
  },
  [ParticleEffectType.DICE_EXPLOSION]: {
    count: 30,
    size: { min: 8, max: 12 },
    speed: { min: 2, max: 4 },
    gravity: 0.2,
    life: { min: 30, max: 60 },
    spread: 360,
    fadeOut: true,
    bounce: true,
    drift: 0.1,
    spin: true,
    emojis: ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'],
  },
  [ParticleEffectType.CONFETTI]: {
    count: 100,
    size: { min: 5, max: 8 },
    speed: { min: 1, max: 3 },
    gravity: 0.15,
    life: { min: 60, max: 90 },
    spread: 60,
    fadeOut: true,
    bounce: false,
    drift: 0.8,
    spin: true,
  },
};